import { useCallback, useMemo } from 'react';

import { allIncludeOptionKeys } from '../../../../shared/reports';
import { useQuery } from '../../../contexts/RouterContext';
import { selectOptionFactory } from '../../../utils';
import { STANDARD_REPORT_OPTIONS } from '../constants';
import type { AttributionReportIncludeOptions, DependencyInfoOption } from '../../../../shared/reports';
import type { SelectOption } from '../../../types';
import type { ReportsV3QueryParams } from '../types';

/**
 * All include-boolean keys that the standard report options define.
 * Used for seeding defaults and building params.
 */
const STANDARD_INCLUDE_KEYS = STANDARD_REPORT_OPTIONS
  .filter((option) => option.kind === 'includeBoolean')
  .map((option) => option.id);

const STANDARD_DEPENDENCY_INFO_OPTIONS: readonly DependencyInfoOption[] =
  STANDARD_REPORT_OPTIONS
    .filter((option) => option.kind === 'dependencyInfoGroup')
    .flatMap((option) => option.dependencyInfoOptions);

/**
 * The default custom option params seeded from the standard report options.
 * Used when the user first switches to the custom flow so they start with the
 * same options as the standard flow.
 *
 * Note: include booleans are stored as the string `'true'` (not boolean `true`)
 * because `qs.parse` always returns strings from the URL. Using strings here
 * keeps the seeded values consistent with what we read back after a URL round-trip,
 * so a single `=== 'true'` check works everywhere.
 */
export const STANDARD_DEFAULTS: Partial<ReportsV3QueryParams> = {
  ...Object.fromEntries(STANDARD_INCLUDE_KEYS.map((key) => [key, 'true'])),
  dependencyInfoOptions: [...STANDARD_DEPENDENCY_INFO_OPTIONS],
};

/**
 * All custom param keys that need to be cleared when switching back to the
 * standard flow — derived from typed sources so nothing can be missed.
 */
type CustomOptionParamKey = keyof AttributionReportIncludeOptions | 'dependencyInfoOptions' | 'excludePackageLabels';

const ALL_CUSTOM_PARAM_KEYS: CustomOptionParamKey[] = [
  ...allIncludeOptionKeys,
  'dependencyInfoOptions',
  'excludePackageLabels',
];

/**
 * The inverse of STANDARD_DEFAULTS — sets every custom param key to `undefined`
 * so `qs.stringify` omits them when switching back to the standard flow.
 */
export const CLEARED_CUSTOM_PARAMS: Record<string, undefined> =
  ALL_CUSTOM_PARAM_KEYS.reduce<Record<string, undefined>>((acc, key) => {
    acc[key] = undefined;
    return acc;
  }, {});

/*
 * excludeUnknownDependencies is the one exclude-style option shown in the
 * include checkbox group: the "Unknown Dependencies" box is checked (included)
 * when the param is absent, and the param is set to 'true' when the box is
 * unchecked. Everywhere outside this display inversion — the URL, the backend
 * params, and saved report configs — the value stays exclude-style.
 */
const isOptionSelected = (key: string, query: ReportsV3QueryParams): boolean => {
  const isSet = query[key as keyof AttributionReportIncludeOptions] === 'true';
  return key === 'excludeUnknownDependencies' ? !isSet : isSet;
};

const toParamValue = (key: string, isSelected: boolean): string | undefined => {
  if (key === 'excludeUnknownDependencies') {
    return isSelected ? undefined : 'true';
  }
  return isSelected ? 'true' : undefined;
};

type CustomReportOptions = {
  includeValues: SelectOption[];
  setIncludeOptions: (values: SelectOption[]) => void;
  licenseValues: SelectOption[];
  setLicenseOptions: (values: SelectOption[]) => void;
  dependencyMetadataValues: SelectOption[];
  setDependencyMetadata: (values: SelectOption[]) => void;
  excludePackageLabelValues: Array<SelectOption<string | number>>;
  setExcludePackageLabels: (values: Array<SelectOption<string | number>>) => void;
  isExcludingLabeledDependencies: boolean;
  setExcludeLabeledDependencies: (isExcluding: boolean) => void;
  setOptionsSelected: (options: CustomOptionKeys, isSelected: boolean) => void;
};

/**
 * Option keys spanning the include, license, and dependency metadata sections,
 * for setting several options across sections in a single URL update.
 */
export type CustomOptionKeys = {
  includeKeys: string[];
  licenseKeys: string[];
  dependencyInfoOptions: DependencyInfoOption[];
};

/**
 * Hook that reads and writes custom report options from/to URL query params.
 * Returns values formatted as SelectOption arrays for use with FilterCard
 * components, and setters that update the URL.
 */
export const useCustomReportOptions = (
  flatOptionKeys: string[],
  licenseOptionKeys: string[],
): CustomReportOptions => {
  const [query, setQuery] = useQuery<ReportsV3QueryParams>();

  const includeValues = useMemo(
    () => flatOptionKeys
      .filter((key) => isOptionSelected(key, query))
      .map((key) => selectOptionFactory(key)),
    [flatOptionKeys, query],
  );

  const licenseValues = useMemo(
    () => licenseOptionKeys
      .filter((key) => query[key as keyof AttributionReportIncludeOptions] === 'true')
      .map((key) => selectOptionFactory(key)),
    [licenseOptionKeys, query],
  );

  const dependencyMetadataValues = useMemo(
    () => (query.dependencyInfoOptions ?? []).map((key) => selectOptionFactory(key)),
    [query.dependencyInfoOptions],
  );

  const excludePackageLabelValues = useMemo(
    () => (query.excludePackageLabels ?? []).map((label) => selectOptionFactory(label)),
    [query.excludePackageLabels],
  );

  const setIncludeOptions = useCallback((values: SelectOption[]) => {
    const selectedSet = new Set(values.map((v) => v.value));
    const update = flatOptionKeys.reduce<Record<string, string | undefined>>((acc, key) => {
      acc[key] = toParamValue(key, selectedSet.has(key));
      return acc;
    }, {});
    setQuery({ ...query, ...update });
  }, [flatOptionKeys, query, setQuery]);

  const setLicenseOptions = useCallback((values: SelectOption[]) => {
    const selectedSet = new Set(values.map((v) => v.value));
    const update = licenseOptionKeys.reduce<Record<string, string | undefined>>((acc, key) => {
      acc[key] = selectedSet.has(key) ? 'true' : undefined;
      return acc;
    }, {});
    setQuery({ ...query, ...update });
  }, [licenseOptionKeys, query, setQuery]);

  const setDependencyMetadata = useCallback((values: SelectOption[]) => {
    const newOptions = values.map((v) => v.value) as DependencyInfoOption[];
    setQuery({ ...query, dependencyInfoOptions: newOptions.length > 0 ? newOptions : undefined });
  }, [query, setQuery]);

  const setExcludePackageLabels = useCallback((values: Array<SelectOption<string | number>>) => {
    const newLabels = values.map((v) => String(v.value));
    setQuery({ ...query, excludePackageLabels: newLabels.length > 0 ? newLabels : undefined });
  }, [query, setQuery]);

  const setExcludeLabeledDependencies = useCallback((isExcluding: boolean) => {
    setQuery({ ...query, excludeLabeledDependencies: isExcluding ? 'true' : undefined });
  }, [query, setQuery]);

  // Selects or deselects the given options in one setQuery call. Calling the
  // per-section setters back to back would drop all but the last update,
  // since each one spreads the same `query`.
  const setOptionsSelected = useCallback((options: CustomOptionKeys, isSelected: boolean) => {
    const includeUpdate = [...options.includeKeys, ...options.licenseKeys]
      .reduce<Record<string, string | undefined>>((acc, key) => {
        acc[key] = toParamValue(key, isSelected);
        return acc;
      }, {});

    const currentDependencyInfoOptions = query.dependencyInfoOptions ?? [];
    const nextDependencyInfoOptions = isSelected
      ? [...new Set([...currentDependencyInfoOptions, ...options.dependencyInfoOptions])]
      : currentDependencyInfoOptions.filter((option) => !options.dependencyInfoOptions.includes(option));

    setQuery({
      ...query,
      ...includeUpdate,
      dependencyInfoOptions: nextDependencyInfoOptions.length > 0 ? nextDependencyInfoOptions : undefined,
    });
  }, [query, setQuery]);

  return {
    includeValues,
    setIncludeOptions,
    licenseValues,
    setLicenseOptions,
    dependencyMetadataValues,
    setDependencyMetadata,
    excludePackageLabelValues,
    setExcludePackageLabels,
    isExcludingLabeledDependencies: query.excludeLabeledDependencies === 'true',
    setExcludeLabeledDependencies,
    setOptionsSelected,
  };
};
